import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface TimerProps {
  initialTime: number;
  isRunning: boolean;
  onComplete: () => void;
  onTick?: (timeLeft: number) => void;
}

const playDingSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Play multiple dings for a more noticeable sound
    const playOneDing = (delay: number) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + delay);
      oscillator.frequency.exponentialRampToValueAtTime(440, audioContext.currentTime + delay + 0.3);

      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime + delay);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + delay + 0.5);

      oscillator.start(audioContext.currentTime + delay);
      oscillator.stop(audioContext.currentTime + delay + 0.5);
    };

    // Three quick dings
    playOneDing(0);
    playOneDing(0.15);
    playOneDing(0.3);
  } catch (e) {
    // Audio not available
  }
};

const Timer: React.FC<TimerProps> = ({ initialTime, isRunning, onComplete, onTick }) => {
  const [timeLeft, setTimeLeft] = React.useState(initialTime);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasPlayedDing = useRef(false);

  useEffect(() => {
    setTimeLeft(initialTime);
    hasPlayedDing.current = false;
  }, [initialTime]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 1;
          if (onTick) onTick(newTime);
          if (newTime <= 0) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (!hasPlayedDing.current) {
              hasPlayedDing.current = true;
              playDingSound();
            }
            onComplete();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, timeLeft > 0]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const isUrgent = timeLeft <= 10;

  return (
    <motion.div
      className={`
        text-6xl font-mono font-bold
        ${isUrgent ? 'text-red-400' : 'text-amber-300'}
      `}
      animate={isUrgent ? {
        scale: [1, 1.1, 1],
        textShadow: [
          '0 0 0 rgba(239, 68, 68, 0)',
          '0 0 20px rgba(239, 68, 68, 0.8)',
          '0 0 0 rgba(239, 68, 68, 0)',
        ],
      } : {}}
      transition={isUrgent ? {
        duration: 0.5,
        repeat: Infinity,
      } : {}}
    >
      {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
    </motion.div>
  );
};

export default Timer;
